/**
 * Статус версии черновика → русское имя и класс пилюли. ОДИН словарь на всё приложение.
 *
 * Имя берётся из `DRAFT_STATE_LABEL_RU` (@smm/contracts, draft-state.ts), а не пишется здесь
 * заново: тот же словарь наполняет справочник `draft_status` в БД. Второй список имён в UI
 * означал бы, что один статус в списке и на экране черновика называется по-разному — причём
 * расхождение проявилось бы не падением, а разными словами в двух местах экрана.
 *
 * Здесь добавляется ровно то, чего в contracts нет и быть не должно: сопоставление статуса
 * с классом пилюли из base.css (docs/ui-design.md §2.1) и группировка статусов для фильтров.
 *
 * `Record<DraftState, …>` вместо словаря с `?? default` намеренно: новое состояние в
 * статусной машине обязано ломать тайпчек здесь, а не молча получать серую пилюлю.
 */

import { DRAFT_STATE_LABEL_RU, DRAFT_STATES, type DraftState } from '@smm/contracts';

/** Классы пилюль из base.css. Свои цвета не заводим — только эти восемь. */
export type PillClass =
  | 'st-new'
  | 'st-wait'
  | 'st-ok'
  | 'st-redo'
  | 'st-hold'
  | 'st-sched'
  | 'st-done'
  | 'st-err';

/**
 * Шкала docs/ui-design.md §2.1. Три состояния, которых в таблице §2.1 нет, разнесены так:
 *   publishing        → st-sched: пост уже в контуре публикации, это та же «синяя» ветка;
 *   needs_manual_check→ st-err:   §2.1 относит «расхождение» к красному, а исход отправки
 *                                 неизвестен — это ровно расхождение, а не предупреждение;
 *   retracted/superseded → st-hold / st-new: обе не требуют действия, но «заменён» уже не
 *                                 актуален, поэтому пунктирная st-new, а не заливка;
 *   discarded         → st-new:   ровно тот же случай, что «заменён», — пост решили не
 *                                 делать, действий нет и не будет. Своего цвета отброшенное
 *                                 не получает: в бэклоге его по умолчанию не видно вовсе.
 */
const PILL_CLASS: Readonly<Record<DraftState, PillClass>> = {
  pending_review: 'st-wait',
  changes_requested: 'st-redo',
  deferred: 'st-hold',
  approved: 'st-ok',
  scheduled: 'st-sched',
  publishing: 'st-sched',
  needs_manual_check: 'st-err',
  published: 'st-done',
  expired: 'st-err',
  failed: 'st-err',
  retracted: 'st-hold',
  superseded: 'st-new',
  discarded: 'st-new',
};

export const statusLabel = (status: DraftState): string => DRAFT_STATE_LABEL_RU[status];

export const statusPillClass = (status: DraftState): PillClass => PILL_CLASS[status];

/**
 * Группы для фильтров-чипов. Двенадцать чипов по числу состояний не поместились бы в колонку
 * 380px и не отвечали бы ни на один реальный вопрос («что мне разбирать?», «что сломалось?»),
 * поэтому близкие исходы сведены — ровно как на макете Backlog.html, где «Ошибки» это один чип.
 *
 * Порядок групп — порядок чипов слева направо: сперва то, что требует работы человека.
 */
export interface StatusGroup {
  readonly id: string;
  readonly label: string;
  readonly states: readonly DraftState[];
}

export const STATUS_GROUPS: readonly StatusGroup[] = [
  { id: 'pending', label: 'Ожидают', states: ['pending_review'] },
  { id: 'rework', label: 'На переделке', states: ['changes_requested'] },
  { id: 'approved', label: 'Одобрены', states: ['approved'] },
  { id: 'scheduled', label: 'Запланированы', states: ['scheduled', 'publishing'] },
  { id: 'published', label: 'Опубликованы', states: ['published'] },
  { id: 'problem', label: 'Проблемы', states: ['needs_manual_check', 'expired', 'failed'] },
  { id: 'deferred', label: 'Отложены', states: ['deferred'] },
  // `discarded` — в «Архиве», а не своим чипом: чип отвечает на вопрос «что мне с этим
  // делать», а с отброшенным делать нечего, ровно как с отозванным и заменённым. Своего
  // чипа он бы к тому же почти никогда не набрал: бэклог отдаёт отброшенное только по
  // явному `?status=discarded` (см. `domain/read-model.ts`, `backlog()`).
  { id: 'archive', label: 'Архив', states: ['retracted', 'superseded', 'discarded'] },
];

/** Идентификатор чипа «все» — не группа, поэтому вне STATUS_GROUPS. */
export const ALL_GROUP_ID = 'all';

const GROUP_OF: ReadonlyMap<DraftState, string> = new Map(
  STATUS_GROUPS.flatMap((g) => g.states.map((s): [DraftState, string] => [s, g.id])),
);

/**
 * `??` здесь недостижим по тесту полноты (status.test.ts), но оставлен: статус приходит по
 * HTTP, и уронить список из-за незнакомого значения хуже, чем показать его в «Архиве».
 */
export const statusGroupId = (status: DraftState): string =>
  GROUP_OF.get(status) ?? STATUS_GROUPS[STATUS_GROUPS.length - 1]?.id ?? ALL_GROUP_ID;

/** Для теста полноты: список состояний, которые обязана покрыть группировка. */
export const ALL_DRAFT_STATES: readonly DraftState[] = DRAFT_STATES;
